import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemButton, { ListItemButtonProps } from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BusinessIcon from '@mui/icons-material/Business';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useCompanyContext, useActiveCompanyId, useCompanyNavigate } from '@lendica/hooks';

export const CompanySwitcher = ({
    expanded = false,
    ...props
}: ListItemButtonProps & {
    expanded?: boolean;
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
    const companyNavigate = useCompanyNavigate();
    const location = useLocation();

    const { memberships, prefetchCompanyDetails, getCompanyName } = useCompanyContext();
    const activeCompanyId = useActiveCompanyId();

    const activeName = getCompanyName(activeCompanyId);
    const activeInitial = (activeName || '?').trim().charAt(0).toUpperCase();

    const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        // Prefetch details so names render quickly in the menu
        try {
            await prefetchCompanyDetails();
        } catch {
            // noop
        }
    };

    const handleSelectCompany = (companyId: string) => {
        setAnchorEl(null);
        // Replace current /c/:companyKey segment with the selected id (slug will normalize if available)
        const path = location.pathname;
        const replaced = path.match(/^\/c\/[^/]+\//)
            ? path.replace(/(^\/c\/)[^/]+/, `$1${companyId}`)
            : `/c/${companyId}/wallet`;
        navigate(replaced + location.search);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Tooltip title="Switch company" placement="right">
                <ListItemButton
                    onClick={handleClick}
                    component="button"
                    {...props}
                    sx={{ width: '100%', borderRadius: expanded ? '8px' : '0px', ...props?.sx }}
                >
                    <ListItemIcon>
                        <Avatar
                            variant="rounded"
                            color="secondary"
                            sx={{
                                width: 24,
                                height: 24,
                                fontSize: 12,
                                backgroundColor: 'secondary.main',
                            }}
                        >
                            {activeInitial}
                        </Avatar>
                    </ListItemIcon>
                    {expanded && (
                        <>
                            <ListItemText primary={activeName} />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pr: 1,
                                }}
                            >
                                <UnfoldMoreIcon fontSize="small" />
                            </Box>
                        </>
                    )}
                </ListItemButton>
            </Tooltip>
            <Menu open={open} onClose={() => setAnchorEl(null)} anchorEl={anchorEl}>
                {memberships.map(m => (
                    <MenuItem
                        key={m.companyId}
                        onClick={() => handleSelectCompany(m.companyId)}
                        sx={{
                            minWidth: '200px',
                        }}
                    >
                        <ListItemIcon>
                            <BusinessIcon fontSize="small" />
                        </ListItemIcon>
                        {getCompanyName(m.companyId)}
                    </MenuItem>
                ))}
                <Divider />
                <MenuItem
                    onClick={() => {
                        companyNavigate('/add-company');
                    }}
                >
                    <ListItemIcon>
                        <AddCircleIcon fontSize="small" />
                    </ListItemIcon>
                    Add company
                </MenuItem>
            </Menu>
        </Box>
    );
};
